import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { createPortal } from 'react-dom';
import TemuOpsPager, { TEMU_OPS_DEFAULT_PAGE_SIZE } from '@/temu-platform/components/TemuOpsPager';
import { temuPlatformAPI } from '@/temu-platform/api/temuPlatformApi';
import { togglePageSelection } from '@/temu-platform/utils/temuPageSelection';
import {
  buildTemuProductTree,
  pickSkcRepresentative,
  sumSkcStock,
} from '@/temu-platform/utils/temuProductTree';
import { buildSiteNameByAbbrMap, resolveSubSiteDisplayName } from '@/temu-platform/utils/temuSiteList';
import SkuDetailModal, {
  skuMerchantCode,
  skuSizeLabel,
} from '@/temu-platform/modules/products/components/SkuDetailModal';
import HoverCopyField from '@/temu-platform/modules/products/components/HoverCopyField';
import TemuProductThumb from '@/temu-platform/modules/products/components/TemuProductThumb';
import { showToast } from '@/utils/toast';
import '@/styles/temu-platform/temu-platform.css';
import '@/styles/temu-platform/temu-product-ops.css';
import '@/styles/ipe/workbench/IpeWorkflowTasksPage.css';
import '@/styles/ipe/workbench/PlatformProductManagePage.css';
import '@/styles/common/empty-state.css';

const SKU_PREVIEW_LIMIT = 1;
const EMPTY_PRODUCT_FILTER = { skc: '', sku: '', spu: '', title: '' };

function isValidSkcId(id) {
  const value = String(id || '').trim();
  return Boolean(value) && value !== '—';
}

function buildProductQuery(draft) {
  const title = String(draft.title || '').trim();
  const skc = String(draft.skc || '').trim();
  const sku = String(draft.sku || '').trim();
  const spu = String(draft.spu || '').trim();
  let idType;
  let keywords;
  if (skc) {
    idType = 'skc';
    keywords = skc;
  } else if (sku) {
    idType = 'merchantSku';
    keywords = sku;
  } else if (spu) {
    idType = 'spu';
    keywords = spu;
  }
  return {
    title: title || undefined,
    idType,
    keywords,
  };
}

function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('zh-CN', { hour12: false });
  } catch {
    return '—';
  }
}

function formatStock(val) {
  if (val == null || val === '') return '—';
  const n = Number(val);
  if (Number.isNaN(n)) return '—';
  return String(n);
}

function isOfflineLike(row) {
  const publish = String(row?.publishStatus || '');
  const audit = String(row?.auditStatus || '');
  const auditLower = audit.toLowerCase();
  return publish === 'offline'
    || audit.trim() === '0'
    || auditLower.includes('off')
    || auditLower.includes('delist')
    || audit.includes('下架');
}

function hasSystemPublishTimes(row) {
  return row?.listingJobLineId != null || row?.sourcingCaptureItemId != null;
}

function shelfStatusLabel(row) {
  if (row?.priceReviewStatus === 'pending') return '待核价';
  const stock = row?.stockQty;
  const stockSoldOut = stock != null && Number(stock) <= 0;
  const hasListedAt = Boolean(row?.listedAt);
  if (isOfflineLike(row)) {
    if (!hasListedAt) return '待上架';
    if (stockSoldOut) return '已售罄';
    return '已下架';
  }
  if (stockSoldOut && row?.publishStatus === 'published' && hasListedAt) return '已售罄';
  const audit = String(row?.auditStatus || '').toLowerCase();
  if (audit.includes('wait') || audit.includes('pend') || audit.includes('待')) return '待上架';
  if (row?.publishStatus === 'published') return '已上架';
  if (row?.publishStatus === 'draft') return '草稿';
  if (row?.publishStatus === 'failed') return '失败';
  return '—';
}


const SHELF_TEXT_KEYS = {
  '待核价': 'certList.pricePending',
  '待上架': 'certList.toList',
  '已售罄': 'certList.soldOut',
  '已下架': 'certList.offShelf',
  '已上架': 'certList.listed',
  '草稿': 'certList.draft',
  '失败': 'certLib.failedShort',
};
function shelfStatusText(row) {
  const raw = shelfStatusLabel(row);
  const id = SHELF_TEXT_KEYS[raw];
  return id ? i18n.t(id, { ns: 'workbench', defaultValue: raw }) : raw;
}
function shelfStatusClass(row) {
  const label = shelfStatusLabel(row);
  if (label === '已上架') return 'success';
  if (label === '待核价' || label === '待上架') return 'processing';
  if (label === '已售罄') return 'waiting';
  if (label === '已下架' || label === '失败') return 'failed';
  return 'waiting';
}

function listedSitesOf(row) {
  const raw = row?.listedSites;
  if (!Array.isArray(raw) || raw.length === 0) return [];
  const seen = new Set();
  const out = [];
  for (const item of raw) {
    const site = String(item || '').trim();
    if (!site || seen.has(site)) continue;
    seen.add(site);
    out.push(site);
  }
  return out;
}

function resolveListedSiteNames(row, siteNameByAbbr) {
  return listedSitesOf(row)
    .map((site) => resolveSubSiteDisplayName(site, siteNameByAbbr))
    .filter(Boolean);
}

function createTimeOf(row) {
  if (!hasSystemPublishTimes(row)) return null;
  return row?.createdAt || null;
}

function publishTimeOf(row) {
  if (!hasSystemPublishTimes(row)) return null;
  return row?.publishedAt || null;
}

function listedTimeOf(row) {
  return row?.listedAt || null;
}

function merchantSkusOf(item) {
  if (Array.isArray(item?.merchantSkus) && item.merchantSkus.length) {
    return item.merchantSkus.map((code) => String(code || '').trim()).filter(Boolean);
  }
  const joined = String(item?.merchantSku || '').trim();
  if (!joined) return [];
  return joined.split(/[、,，]/).map((code) => code.trim()).filter(Boolean);
}

function collectPageSkcIds(tree) {
  const ids = [];
  const seen = new Set();
  for (const spu of tree || []) {
    for (const skc of spu.skcs || []) {
      if (!isValidSkcId(skc.skcId) || seen.has(skc.skcId)) continue;
      seen.add(skc.skcId);
      ids.push(skc.skcId);
    }
  }
  return ids;
}

/**
 * 证书库绑定 SKC：左已绑定（默认全勾，取消勾选确认=解绑），右选品勾选=待绑定。
 */
export default function CertificateBindProductModal({ open, shopId, row, onClose, onDone }) {
  const { t } = useTranslation('workbench');
  const poolSn = String(row?.poolSn || '').trim();
  const keepInitRef = useRef(false);

  const [saving, setSaving] = useState(false);
  const [leftKeywordDraft, setLeftKeywordDraft] = useState('');
  const [leftKeyword, setLeftKeyword] = useState('');
  const [leftPage, setLeftPage] = useState(1);
  const [leftPageSize, setLeftPageSize] = useState(TEMU_OPS_DEFAULT_PAGE_SIZE);
  const [leftLoading, setLeftLoading] = useState(false);
  const [leftItems, setLeftItems] = useState([]);
  const [leftTotal, setLeftTotal] = useState(0);
  const [boundAllNames, setBoundAllNames] = useState([]);
  const [keepSet, setKeepSet] = useState(() => new Set());

  const [filterDraft, setFilterDraft] = useState(EMPTY_PRODUCT_FILTER);
  const [productQuery, setProductQuery] = useState(EMPTY_PRODUCT_FILTER);
  const [rightPage, setRightPage] = useState(1);
  const [rightPageSize, setRightPageSize] = useState(TEMU_OPS_DEFAULT_PAGE_SIZE);
  const [rightLoading, setRightLoading] = useState(false);
  const [rightItems, setRightItems] = useState([]);
  const [rightTotal, setRightTotal] = useState(0);
  const [selectedSkcs, setSelectedSkcs] = useState(() => new Set());
  const [skuModalSkc, setSkuModalSkc] = useState(null);
  const [openingBoundSkc, setOpeningBoundSkc] = useState('');
  const [siteNameByAbbr, setSiteNameByAbbr] = useState(() => new Map());

  const boundSet = useMemo(() => new Set(boundAllNames), [boundAllNames]);
  const productTree = useMemo(() => buildTemuProductTree(rightItems), [rightItems]);
  const pageSkcIds = useMemo(() => collectPageSkcIds(productTree), [productTree]);
  const selectablePageSkcIds = useMemo(
    () => pageSkcIds.filter((id) => !boundSet.has(id)),
    [pageSkcIds, boundSet],
  );

  const leftPageKeys = useMemo(
    () => leftItems.map((item) => String(item?.skcName || '').trim()).filter(Boolean),
    [leftItems],
  );
  const leftPageKeptCount = leftPageKeys.filter((key) => keepSet.has(key)).length;
  const leftAllPageKept = leftPageKeys.length > 0 && leftPageKeptCount === leftPageKeys.length;
  const leftSomePageKept = leftPageKeptCount > 0 && !leftAllPageKept;

  const rightAllPageSelected = selectablePageSkcIds.length > 0
    && selectablePageSkcIds.every((id) => selectedSkcs.has(id));
  const rightSomePageSelected = selectablePageSkcIds.some((id) => selectedSkcs.has(id)) && !rightAllPageSelected;

  const bindCount = useMemo(
    () => [...selectedSkcs].filter((id) => id && !boundSet.has(id)).length,
    [selectedSkcs, boundSet],
  );
  const unbindCount = useMemo(
    () => boundAllNames.filter((id) => !keepSet.has(id)).length,
    [boundAllNames, keepSet],
  );

  const resetState = useCallback(() => {
    keepInitRef.current = false;
    setSaving(false);
    setLeftKeywordDraft('');
    setLeftKeyword('');
    setLeftPage(1);
    setLeftPageSize(TEMU_OPS_DEFAULT_PAGE_SIZE);
    setLeftItems([]);
    setLeftTotal(0);
    setBoundAllNames([]);
    setKeepSet(new Set());
    setFilterDraft(EMPTY_PRODUCT_FILTER);
    setProductQuery(EMPTY_PRODUCT_FILTER);
    setRightPage(1);
    setRightPageSize(TEMU_OPS_DEFAULT_PAGE_SIZE);
    setRightItems([]);
    setRightTotal(0);
    setSelectedSkcs(new Set());
    setSkuModalSkc(null);
    setOpeningBoundSkc('');
  }, []);

  useEffect(() => {
    if (!open) {
      resetState();
      return undefined;
    }
    const onKey = (e) => {
      if (e.key === 'Escape' && !saving) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, saving, onClose, resetState]);

  useEffect(() => {
    let cancelled = false;
    async function loadSiteNames() {
      if (!open || !shopId) {
        setSiteNameByAbbr(new Map());
        return;
      }
      try {
        const res = await temuPlatformAPI.getShopSiteList(Number(shopId));
        if (!cancelled) setSiteNameByAbbr(buildSiteNameByAbbrMap(res?.siteList ?? res));
      } catch {
        if (!cancelled) setSiteNameByAbbr(new Map());
      }
    }
    loadSiteNames();
    return () => { cancelled = true; };
  }, [open, shopId]);

  useEffect(() => {
    if (!open || !shopId || !poolSn) return undefined;
    let cancelled = false;
    async function loadLeft() {
      setLeftLoading(true);
      try {
        const res = await temuPlatformAPI.listCertificateSkcBinds(shopId, {
          poolSn,
          keyword: leftKeyword || undefined,
          page: leftPage,
          pageSize: leftPageSize,
        });
        if (cancelled) return;
        const names = Array.isArray(res?.boundSkcNames)
          ? res.boundSkcNames.map((s) => String(s || '').trim()).filter(Boolean)
          : [];
        setBoundAllNames(names);
        setLeftItems(Array.isArray(res?.items) ? res.items : []);
        setLeftTotal(Number(res?.total) || 0);
        if (!keepInitRef.current) {
          setKeepSet(new Set(names));
          keepInitRef.current = true;
        }
      } catch (e) {
        if (!cancelled) {
          showToast(e?.message || t('certList.loadBoundFailed'), 'error');
          setLeftItems([]);
          setLeftTotal(0);
        }
      } finally {
        if (!cancelled) setLeftLoading(false);
      }
    }
    loadLeft();
    return () => { cancelled = true; };
  }, [open, shopId, poolSn, leftKeyword, leftPage, leftPageSize, t]);

  useEffect(() => {
    if (!open || !shopId) return undefined;
    let cancelled = false;
    async function loadRight() {
      setRightLoading(true);
      try {
        const query = buildProductQuery(productQuery);
        const res = await temuPlatformAPI.listProducts({
          shopId,
          page: rightPage,
          pageSize: rightPageSize,
          title: query.title,
          idType: query.idType,
          keywords: query.keywords,
        });
        if (cancelled) return;
        setRightItems(Array.isArray(res?.items) ? res.items : []);
        setRightTotal(Number(res?.total) || 0);
      } catch (e) {
        if (!cancelled) {
          showToast(e?.message || t('certList.loadProductsFailed'), 'error');
          setRightItems([]);
          setRightTotal(0);
        }
      } finally {
        if (!cancelled) setRightLoading(false);
      }
    }
    loadRight();
    return () => { cancelled = true; };
  }, [open, shopId, productQuery, rightPage, rightPageSize, t]);

  const openBoundSkuModal = async (item) => {
    const skcName = String(item?.skcName || '').trim();
    if (!shopId || !skcName || openingBoundSkc) return;
    setOpeningBoundSkc(skcName);
    try {
      const res = await temuPlatformAPI.listProducts({
        shopId,
        idType: 'skc',
        keywords: skcName,
        page: 1,
        pageSize: 100,
      });
      const tree = buildTemuProductTree(Array.isArray(res?.items) ? res.items : []);
      const skc = tree
        .flatMap((spu) => spu.skcs || [])
        .find((node) => String(node?.skcId || '').trim() === skcName)
        || tree[0]?.skcs?.[0];
      if (skc) {
        setSkuModalSkc(skc);
        return;
      }
      const codes = merchantSkusOf(item);
      setSkuModalSkc({
        key: skcName,
        skcId: skcName,
        specLabel: item?.title || '',
        image: item?.imageUrl || null,
        skus: codes.map((code, idx) => ({
          id: `${skcName}-merchant-${idx}`,
          supplierSku: code,
          merchantSku: code,
        })),
      });
    } catch (e) {
      showToast(e?.message || t('certList.loadSkuFailed'), 'error');
    } finally {
      setOpeningBoundSkc('');
    }
  };

  const toggleKeep = (skcName) => {
    if (!skcName || saving) return;
    setKeepSet((prev) => {
      const next = new Set(prev);
      if (next.has(skcName)) next.delete(skcName);
      else next.add(skcName);
      return next;
    });
  };

  const toggleLeftPage = () => {
    if (!leftPageKeys.length || saving) return;
    setKeepSet((prev) => togglePageSelection(prev, leftPageKeys, leftAllPageKept));
  };

  const toggleSelected = (skcId) => {
    if (!isValidSkcId(skcId) || boundSet.has(skcId) || saving) return;
    setSelectedSkcs((prev) => {
      const next = new Set(prev);
      if (next.has(skcId)) next.delete(skcId);
      else next.add(skcId);
      return next;
    });
  };

  const toggleRightPage = () => {
    if (!selectablePageSkcIds.length || saving) return;
    setSelectedSkcs((prev) => togglePageSelection(prev, selectablePageSkcIds, rightAllPageSelected));
  };

  const onQueryLeft = () => {
    setLeftPage(1);
    setLeftKeyword(leftKeywordDraft.trim());
  };

  const onQueryRight = () => {
    setRightPage(1);
    setProductQuery({ ...filterDraft });
  };

  const onResetRight = () => {
    setFilterDraft(EMPTY_PRODUCT_FILTER);
    setProductQuery(EMPTY_PRODUCT_FILTER);
    setRightPage(1);
    setSelectedSkcs(new Set());
  };

  const submit = async () => {
    if (!shopId || !poolSn || saving) return;
    const bindSkcNames = [...selectedSkcs].filter((id) => isValidSkcId(id) && !boundSet.has(id));
    const unbindSkcNames = boundAllNames.filter((id) => !keepSet.has(id));
    if (bindSkcNames.length === 0 && unbindSkcNames.length === 0) {
      showToast(t('certList.pickSkc'), 'warning');
      return;
    }
    setSaving(true);
    try {
      const res = await temuPlatformAPI.applyCertificateSkcBinds(shopId, {
        poolSn,
        bindSkcNames,
        unbindSkcNames,
      });
      const bound = Number(res?.bound) || 0;
      const unbound = Number(res?.unbound) || 0;
      const errors = Array.isArray(res?.errors) ? res.errors.filter(Boolean) : [];
      if (bound === 0 && unbound === 0) {
        showToast(errors[0] || t('certList.noChange'), 'error');
        return;
      }
      const parts = [];
      if (bound > 0) parts.push(t('certList.boundSkc', { count: bound }));
      if (unbound > 0) {
        parts.push(t('certList.unboundSkc', { count: unbound }));
      }
      let message = parts.join('，');
      if (unbound > 0 || res?.unbindLocalOnly) {
        message = `${message}。${t('certList.unbindHintTemu')}`;
      }
      if (errors.length) {
        message = `${message}；${t('certList.partialFail', { detail: errors[0] })}`;
      }
      showToast(message, errors.length ? 'warning' : 'success');
      onDone?.();
      onClose?.();
    } catch (e) {
      showToast(e?.message || t('rrpOps.submitFailed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!open || !row) return null;

  return createPortal(
    <div className="tpo-batch-modal tpo-cert-modal tpo-cert-bind-modal temu-product-ops" role="presentation">
      <button
        type="button"
        className="tpo-batch-modal__backdrop"
        aria-label={t('moduleShell.close')}
        onClick={() => { if (!saving) onClose?.(); }}
      />
      <div
        className="tpo-batch-modal__panel tpo-cert-modal__panel tpo-cert-bind-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tpo-cert-bind-title"
      >
        <div className="tpo-batch-modal__head">
          <h2 id="tpo-cert-bind-title">{t('certList.bindTitle', { no: poolSn || '—' })}</h2>
          <button type="button" className="tpo-btn tpo-btn--ghost tpo-btn--sm" onClick={onClose} disabled={saving}>
            {t('moduleShell.close')}
          </button>
        </div>
        <div className="tpo-batch-modal__body tpo-cert-bind-modal__body">
          <section className="tpo-cert-bind-pane tpo-cert-bind-pane--left">
            <div className="tpo-cert-bind-pane__toolbar">
              <h3 className="tpo-cert-bind-pane__title">{t('certList.bound')}</h3>
              <span className="tpo-cert-bind-pane__meta">{t('certList.boundMeta', { total: leftTotal, keep: keepSet.size })}</span>
            </div>
            <div className="tpo-cert-bind-pane__filters">
              <input
                type="text"
                className="tpo-input"
                placeholder={t('certList.boundSearchPh')}
                value={leftKeywordDraft}
                disabled={saving}
                onChange={(e) => setLeftKeywordDraft(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onQueryLeft()}
              />
              <button type="button" className="tpo-btn tpo-btn--secondary tpo-btn--sm" onClick={onQueryLeft} disabled={saving || leftLoading}>
                {t('returnList.query')}
              </button>
            </div>
            <div className="tpo-cert-bind-pane__table-wrap">
              <table className="tpo-certs-table tpo-cert-bind-left-table">
                <thead>
                  <tr>
                    <th
                      scope="col"
                      className="tpo-cert-bind-left-table__check"
                      onClick={() => {
                        if (saving || leftPageKeys.length === 0) return;
                        toggleLeftPage();
                      }}
                    >
                      <input
                        type="checkbox"
                        aria-label={t('certList.selectBound')}
                        checked={leftAllPageKept}
                        disabled={saving || leftPageKeys.length === 0}
                        ref={(el) => {
                          if (!el) return;
                          el.indeterminate = leftSomePageKept;
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onChange={toggleLeftPage}
                      />
                    </th>
                    <th scope="col">{t('certList.goods')}</th>
                    <th scope="col">{t('certList.merchantSku')}</th>
                  </tr>
                </thead>
                <tbody>
                  {leftLoading && leftItems.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="tpo-certs-empty-cell">{t('returnList.loading')}</td>
                    </tr>
                  ) : leftItems.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="tpo-certs-empty-cell">
                        <div className="tpo-certs-empty">
                          <p className="tpo-certs-empty__title">{t('certList.noBound')}</p>
                          <p className="tpo-certs-empty__desc">{t('certList.noBoundDesc')}</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    leftItems.map((item) => {
                      const skcName = String(item?.skcName || '').trim();
                      const kept = keepSet.has(skcName);
                      return (
                        <tr key={`${item.id || skcName}`} className={kept ? '' : 'is-unbind'}>
                          <td
                            className="tpo-cert-bind-left-table__check"
                            onClick={() => {
                              if (saving || !skcName) return;
                              toggleKeep(skcName);
                            }}
                          >
                            <input
                              type="checkbox"
                              aria-label={t('certList.keepBind', { name: skcName })}
                              checked={kept}
                              disabled={saving || !skcName}
                              onClick={(e) => e.stopPropagation()}
                              onChange={() => toggleKeep(skcName)}
                            />
                          </td>
                          <td>
                            <div className="tpo-photo-product">
                              <TemuProductThumb imageUrl={item.imageUrl} alt={item.title || skcName} />
                              <div className="tpo-photo-product__meta">
                                <div className="tpo-photo-product__title" title={item.title || ''}>
                                  {item.title || '—'}
                                </div>
                                {item.spuName ? (
                                  <div className="tpo-photo-product__sub">SPU {item.spuName}</div>
                                ) : null}
                              </div>
                            </div>
                          </td>
                          <td>
                            <BoundMerchantSkuPreview
                              item={item}
                              opening={openingBoundSkc === skcName}
                              disabled={saving}
                              onOpen={() => openBoundSkuModal(item)}
                            />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            <TemuOpsPager
              page={leftPage}
              pageSize={leftPageSize}
              total={leftTotal}
              loading={leftLoading}
              disabled={saving}
              onPageChange={setLeftPage}
              onPageSizeChange={(size) => {
                setLeftPageSize(size);
                setLeftPage(1);
              }}
              ariaLabel={t('certList.boundPager')}
            />
          </section>

          <section className="tpo-cert-bind-pane tpo-cert-bind-pane--right">
            <div className="tpo-cert-bind-pane__toolbar">
              <h3 className="tpo-cert-bind-pane__title">{t('certList.pickGoods')}</h3>
              <span className="tpo-cert-bind-pane__meta">{t('certList.pendingBind', { count: bindCount })}</span>
            </div>
            <div className="tpo-cert-bind-pane__filters tpo-cert-bind-pane__filters--right">
              <input
                type="text"
                className="tpo-input"
                placeholder="SKC"
                value={filterDraft.skc}
                disabled={saving}
                onChange={(e) => setFilterDraft((prev) => ({ ...prev, skc: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && onQueryRight()}
              />
              <input
                type="text"
                className="tpo-input"
                placeholder={t('certList.skuPh')}
                value={filterDraft.sku}
                disabled={saving}
                onChange={(e) => setFilterDraft((prev) => ({ ...prev, sku: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && onQueryRight()}
              />
              <input
                type="text"
                className="tpo-input"
                placeholder="SPU"
                value={filterDraft.spu}
                disabled={saving}
                onChange={(e) => setFilterDraft((prev) => ({ ...prev, spu: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && onQueryRight()}
              />
              <input
                type="text"
                className="tpo-input"
                placeholder={t('certList.namePh')}
                value={filterDraft.title}
                disabled={saving}
                onChange={(e) => setFilterDraft((prev) => ({ ...prev, title: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && onQueryRight()}
              />
              <button type="button" className="tpo-btn tpo-btn--secondary tpo-btn--sm" onClick={onQueryRight} disabled={saving || rightLoading}>
                {t('returnList.query')}
              </button>
              <button type="button" className="tpo-btn tpo-btn--secondary tpo-btn--sm" onClick={onResetRight} disabled={saving || rightLoading}>
                {t('returnList.reset')}
              </button>
            </div>
            <div className="wf-tasks-table-panel tpo-cert-bind-pane__table-wrap">
              <div className="wf-tasks-table-wrap">
                <table className="wf-tasks-table platform-product-table platform-product-table--temu">
                  <colgroup>
                    <col className="temu-product-col--check" />
                    <col className="temu-product-col--info" />
                    <col className="temu-product-col--skc" />
                    <col className="temu-product-col--merchant" />
                    <col className="temu-product-col--sku" />
                    <col className="temu-product-col--stock" />
                    <col className="temu-product-col--status" />
                    <col className="temu-product-col--sites" />
                    <col className="temu-product-col--time" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th
                        scope="col"
                        className="temu-product-cell--spu-check"
                        onClick={() => {
                          if (saving || selectablePageSkcIds.length === 0) return;
                          toggleRightPage();
                        }}
                      >
                        <input
                          type="checkbox"
                          aria-label={t('certList.selectUnbound')}
                          checked={rightAllPageSelected}
                          disabled={saving || selectablePageSkcIds.length === 0}
                          ref={(el) => {
                            if (!el) return;
                            el.indeterminate = rightSomePageSelected;
                          }}
                          onClick={(e) => e.stopPropagation()}
                          onChange={toggleRightPage}
                        />
                      </th>
                      <th scope="col">{t('certList.colProduct')}</th>
                      <th scope="col">{t('certList.mainSkc')}</th>
                      <th scope="col">{t('certList.merchantSku')}</th>
                      <th scope="col">SKU</th>
                      <th scope="col">{t('certList.stock')}</th>
                      <th scope="col">{t('rrpOps.status')}</th>
                      <th scope="col">{t('rrpOps.site')}</th>
                      <th scope="col">{t('certLib.colTime')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {renderProductBody({
                      t,
                      loading: rightLoading,
                      tree: productTree,
                      boundSet,
                      selectedSkcs,
                      saving,
                      siteNameByAbbr,
                      onToggle: toggleSelected,
                      onOpenSku: setSkuModalSkc,
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            <TemuOpsPager
              page={rightPage}
              pageSize={rightPageSize}
              total={rightTotal}
              loading={rightLoading}
              disabled={saving}
              onPageChange={setRightPage}
              onPageSizeChange={(size) => {
                setRightPageSize(size);
                setRightPage(1);
              }}
              ariaLabel={t('certList.productPager')}
            />
          </section>
        </div>
        <div className="tpo-batch-modal__foot">
          <p className="tpo-batch-modal__hint">
            {t('certList.unbindHintTemu')}
            {t('certList.rightHint')}
            {bindCount || unbindCount ? t('certList.thisChange', { bind: bindCount, unbind: unbindCount }) : ''}
          </p>
          <div className="tpo-cert-bind-modal__actions">
            <button type="button" className="tpo-btn tpo-btn--secondary tpo-btn--sm" onClick={onClose} disabled={saving}>
              {t('shop.cancel')}
            </button>
            <button type="button" className="tpo-btn tpo-btn--primary tpo-btn--sm" onClick={submit} disabled={saving}>
              {saving ? t('returnList.submitting') : t('productTemplate.confirm')}
            </button>
          </div>
        </div>
      </div>
      <SkuDetailModal open={Boolean(skuModalSkc)} skc={skuModalSkc} onClose={() => setSkuModalSkc(null)} />
    </div>,
    document.body,
  );
}

function renderProductBody({
  t,
  loading,
  tree,
  boundSet,
  selectedSkcs,
  saving,
  siteNameByAbbr,
  onToggle,
  onOpenSku,
}) {
  if (loading && tree.length === 0) {
    return (
      <tr>
        <td colSpan={10} className="wf-tasks-table__empty">{t('returnList.loading')}</td>
      </tr>
    );
  }
  if (tree.length === 0) {
    return (
      <tr>
        <td colSpan={10} className="wf-tasks-table__empty">
          <div className="funding-empty-state">
            <p className="funding-empty-state__title">{t('certList.noProducts')}</p>
            <p className="funding-empty-state__description">{t('certList.adjustFilter')}</p>
          </div>
        </td>
      </tr>
    );
  }

  const rows = [];
  tree.forEach((spu) => {
    const skcList = spu.skcs.length
      ? spu.skcs
      : [{ key: `${spu.key}-empty`, skcId: '—', image: null, specLabel: '', skus: [] }];
    const spuRowSpan = Math.max(1, skcList.length);
    let spuRowStarted = false;

    skcList.forEach((skc, skcIdx) => {
      const isFirstSpuRow = !spuRowStarted;
      if (isFirstSpuRow) spuRowStarted = true;
      const representative = pickSkcRepresentative(skc.skus);
      const stockTotal = sumSkcStock(skc.skus, 'stockAvailableQty');
      const previewSkus = (skc.skus || []).slice(0, SKU_PREVIEW_LIMIT);
      const hasMoreSkus = (skc.skus || []).length > SKU_PREVIEW_LIMIT;
      const skcId = isValidSkcId(skc.skcId) ? skc.skcId : '';
      const alreadyBound = Boolean(skcId && boundSet.has(skcId));
      const checked = Boolean(skcId && selectedSkcs.has(skcId));
      const rowKey = `${spu.key}-${skc.key}-${skcIdx}`;

      rows.push(
        <tr key={rowKey} className="temu-product-row">
          <td
            className={`temu-product-cell temu-product-cell--spu-check${alreadyBound ? ' is-bound' : ''}`}
            onClick={() => {
              if (alreadyBound || saving || !skcId) return;
              onToggle(skcId);
            }}
          >
            {alreadyBound ? (
              <span className="tpo-cert-bind-bound-tag">{t('certList.bound')}</span>
            ) : (
              <input
                type="checkbox"
                aria-label={t('certList.selectSkc', { name: skcId || '—' })}
                checked={checked}
                disabled={saving || !skcId}
                onClick={(e) => e.stopPropagation()}
                onChange={() => onToggle(skcId)}
              />
            )}
          </td>
          {isFirstSpuRow ? (
            <td rowSpan={spuRowSpan} className="temu-product-cell temu-product-cell--info">
              <div className="temu-product-info temu-product-info--text-only">
                <div className="temu-product-info__text">
                  <HoverCopyField value={spu.title} className="temu-product-info__title" popoverCopy />
                  <HoverCopyField
                    label="SPU"
                    value={spu.spuId && spu.spuId !== '—' ? spu.spuId : ''}
                    className="temu-product-info__meta"
                  />
                </div>
              </div>
            </td>
          ) : null}
          <td className="temu-product-cell temu-product-cell--spec">
            <div className="temu-product-spec">
              <TemuProductThumb imageUrl={skc.image} alt={skc.specLabel || 'SKC'} />
              <div className="temu-product-spec__text">
                {skc.specLabel ? <div className="temu-product-spec__color">{skc.specLabel}</div> : null}
                <HoverCopyField
                  label="SKC"
                  value={skcId}
                  className="temu-product-spec__skc"
                />
                <HoverCopyField
                  label={t('tiktokCatalog.supplier')}
                  value={skc.supplierCode || ''}
                  className="temu-product-spec__skc"
                  emptyText="—"
                />
              </div>
            </div>
          </td>
          <td className="temu-product-cell temu-product-cell--merchant-sku">
            <SkuPreview
              previewSkus={previewSkus}
              hasMoreSkus={hasMoreSkus}
              skc={skc}
              mode="merchant"
              onOpenSku={onOpenSku}
            />
          </td>
          <td className="temu-product-cell temu-product-cell--sku">
            <SkuPreview
              previewSkus={previewSkus}
              hasMoreSkus={hasMoreSkus}
              skc={skc}
              mode="sku"
              onOpenSku={onOpenSku}
            />
          </td>
          <td className="temu-product-cell temu-product-cell--stock">
            {formatStock(stockTotal)}
          </td>
          <td className="temu-product-cell temu-product-cell--status">
            {representative ? (
              (() => {
                const statusRow = {
                  ...representative,
                  stockQty: stockTotal != null ? stockTotal : representative.stockQty,
                  listedAt: (skc.skus || []).find((s) => s?.listedAt)?.listedAt || representative.listedAt,
                };
                return (
                  <div className="temu-product-status-block">
                    <span className={`temu-product-shelf temu-product-shelf--${shelfStatusClass(statusRow)}`}>
                      {shelfStatusText(statusRow)}
                    </span>
                  </div>
                );
              })()
            ) : '—'}
          </td>
          <td className="temu-product-cell temu-product-cell--sites">
            {representative ? (
              (() => {
                const siteNames = resolveListedSiteNames(representative, siteNameByAbbr);
                if (!siteNames.length) return '—';
                return (
                  <div className="temu-product-sites" title={siteNames.join('\n')}>
                    {siteNames.map((name) => (
                      <div key={name} className="temu-product-sites__item">{name}</div>
                    ))}
                  </div>
                );
              })()
            ) : '—'}
          </td>
          <td className="temu-product-cell temu-product-cell--time">
            {representative ? (
              (() => {
                const timeRow = {
                  ...representative,
                  listedAt: (skc.skus || []).find((s) => s?.listedAt)?.listedAt || representative.listedAt,
                };
                return (
                  <>
                    <div>{t('certList.createdOn', { time: formatDateTime(createTimeOf(timeRow)) })}</div>
                    <div>{t('certList.publishedOn', { time: formatDateTime(publishTimeOf(timeRow)) })}</div>
                    <div>{t('certList.listedOn', { time: formatDateTime(listedTimeOf(timeRow)) })}</div>
                  </>
                );
              })()
            ) : '—'}
          </td>
        </tr>,
      );
    });
  });
  return rows;
}

function BoundMerchantSkuPreview({ item, opening, disabled, onOpen }) {
  const { t } = useTranslation('workbench');
  const codes = merchantSkusOf(item);
  const skuCount = Number(item?.skuCount) > 0 ? Number(item.skuCount) : codes.length;
  if (!codes.length) {
    return <div className="temu-product-sku-preview__item temu-product-sku-preview__item--empty">—</div>;
  }
  return (
    <div className="temu-product-sku-preview">
      <div className="temu-product-sku-preview__item">
        <HoverCopyField
          value={codes[0]}
          className="temu-product-sku-preview__code"
        />
      </div>
      {skuCount > 1 ? (
        <button
          type="button"
          className="temu-product-sku-preview__more"
          disabled={disabled || opening}
          onClick={onOpen}
        >
          {opening ? t('returnList.loading') : t('certList.viewMore', { count: skuCount })}
        </button>
      ) : null}
    </div>
  );
}

function SkuPreview({ previewSkus, hasMoreSkus, skc, mode, onOpenSku }) {
  const { t } = useTranslation('workbench');
  if (!previewSkus.length) {
    return <div className="temu-product-sku-preview__item temu-product-sku-preview__item--empty">—</div>;
  }
  return (
    <div className="temu-product-sku-preview">
      {previewSkus.map((sku, idx) => (
        <div key={sku?.id || `${skc.key}-${mode}-${idx}`} className="temu-product-sku-preview__item">
          {mode === 'sku' ? (
            <>
              <div className="temu-product-sku-preview__size">{t('certList.sizeLine', { size: skuSizeLabel(sku) })}</div>
              <HoverCopyField
                label="SKU"
                value={sku?.platformSkuId || ''}
                className="temu-product-sku-preview__code"
              />
            </>
          ) : (
            <HoverCopyField
              value={skuMerchantCode(sku)}
              className="temu-product-sku-preview__code"
            />
          )}
        </div>
      ))}
      {hasMoreSkus ? (
        <button
          type="button"
          className="temu-product-sku-preview__more"
          onClick={() => onOpenSku(skc)}
        >
          {t('certList.viewMore', { count: (skc.skus || []).length })}
        </button>
      ) : null}
    </div>
  );
}
